import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { Breakpoint, useBreakpoint } from '@jsmdg/yoshi';
import { CreativePlaceholderId } from '../../shared/enums/creativePlaceholderId';
import { type CampaignCreative } from '../../shared/types/campaignCreative';
import { type DynamicYieldCampaign } from '../../shared/types/dynamicYieldCampaign';
import { type FragmentContext } from '../../shared/types/fragmentContext';

const heroStageBannerIds = [
    CreativePlaceholderId.HeroStageDesktop,
    CreativePlaceholderId.HeroStageMobile,
    CreativePlaceholderId.HeroBanner,
];

type WithPlaceholder = {
    placeholderId?: CreativePlaceholderId;
    name?: CreativePlaceholderId | string;
};

function getByPlaceholder<T extends WithPlaceholder>(
    items: T[] | undefined,
    placeholderId: CreativePlaceholderId,
): T | undefined {
    return items?.find(item => item.placeholderId === placeholderId || item.name === placeholderId);
}

type UseCreatives = {
    navigationCreatives: CampaignCreative[];
    heroStageCreatives: CampaignCreative[];
    dynamicYieldHeroStageCreatives: DynamicYieldCampaign[];
};

export const useCreatives = (): UseCreatives => {
    const { campaignCreatives } = useFragmentContext<FragmentContext>();
    const isDesktop = useBreakpoint(Breakpoint.MD);

    const navigationCreatives = isDesktop
        ? (campaignCreatives?.creatives || []).filter(
              campaignCreative =>
                  campaignCreative.placeholderId === CreativePlaceholderId.NavigationBannerDesktop,
          )
        : [];

    const heroStageCreatives = heroStageBannerIds
        .map(placeholderId => getByPlaceholder(campaignCreatives?.creatives, placeholderId))
        .filter(creative => !!creative) as CampaignCreative[];

    const dynamicYieldHeroStageCreatives = heroStageBannerIds
        .map(placeholderId => getByPlaceholder(campaignCreatives?.dynamicYield, placeholderId))
        .filter(creative => !!creative) as DynamicYieldCampaign[];

    return {
        navigationCreatives,
        heroStageCreatives,
        dynamicYieldHeroStageCreatives,
    };
};
